import React, { useMemo } from "react";
import { Link, useLocation } from "react-router-dom";
import { useDispatch } from "react-redux";
import {
  products,
  more_products,
  GANESH_SEASON_ENABLED,
  DIWALI_SEASON_ENABLED,
} from "../../data/Data";
import { addToCart, getCartTotal } from "../../redux/CartSlice";
import { CATEGORY_BY_PATH } from "../../seo/categories";
import Seo, { SITE_URL } from "../../components/Seo";
import NotFoundPage from "../NotFoundPage/NotFoundPage";
import ScrollToTop from "../../components/ScrollToTop";
import "../../css/components.css";
import "./category.css";

/**
 * A category landing page — one real URL per category.
 *
 * This is not `/shop` with a filter applied. The grid is the same, but the page
 * also carries an H1, an intro that explains what the format is and who it
 * suits, and an FAQ block marked up as FAQPage. Without that copy the page is a
 * product grid with a heading, which Google reads as a thin duplicate of /shop
 * and declines to rank — the same problem `?category=` had.
 *
 * Which category a page shows is resolved from the URL rather than a prop, so
 * routing stays declarative in App.js.
 */
export default function CategoryPage() {
  const { pathname } = useLocation();
  const dispatch = useDispatch();

  // Tolerate a trailing slash so /dhoop-sticks/ resolves like /dhoop-sticks.
  const path =
    pathname !== "/" && pathname.endsWith("/") ? pathname.slice(0, -1) : pathname;
  const cat = CATEGORY_BY_PATH[path];

  const items = useMemo(() => {
    if (!cat) return [];
    return [...products, ...more_products].filter(
      (item) =>
        item.category === cat.slug &&
        (!item.isGaneshSeasonal || GANESH_SEASON_ENABLED) &&
        (!item.isDiwaliSeasonal || DIWALI_SEASON_ENABLED)
    );
  }, [cat]);

  if (!cat) return <NotFoundPage />;

  const handleAddToCart = (e, product) => {
    e.stopPropagation();
    if (product.out_of_stock) return;
    dispatch(addToCart({ ...product, quantity: 1, totalPrice: Number(product.price) }));
    dispatch(getCartTotal());
  };

  const discountOf = (price, mainPrice) => {
    const mrp = Number(mainPrice);
    const now = Number(price);
    if (!mrp || mrp <= now) return null;
    return Math.round(((mrp - now) / mrp) * 100);
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: SITE_URL },
      { "@type": "ListItem", position: 2, name: "Shop", item: `${SITE_URL}/shop` },
      { "@type": "ListItem", position: 3, name: cat.name, item: `${SITE_URL}${cat.path}` },
    ],
  };

  const faqSchema = cat.faqs?.length
    ? {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: cat.faqs.map((f) => ({
          "@type": "Question",
          name: f.q,
          acceptedAnswer: { "@type": "Answer", text: f.a },
        })),
      }
    : null;

  // ItemList tells Google what the grid contains without claiming each entry is
  // a full Product — the Product markup lives on the item pages themselves.
  const listSchema = items.length
    ? {
        "@context": "https://schema.org",
        "@type": "ItemList",
        itemListElement: items.map((item, i) => ({
          "@type": "ListItem",
          position: i + 1,
          url: `${SITE_URL}/item/${item.product_name_key}`,
          name: item.product_name,
        })),
      }
    : null;

  return (
    <>
      <ScrollToTop />
      <Seo title={cat.title} description={cat.description} image={cat.image}>
        <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
        {listSchema && (
          <script type="application/ld+json">{JSON.stringify(listSchema)}</script>
        )}
        {faqSchema && (
          <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
        )}
      </Seo>

      <div className="category-page">
        <div className="container">
          <nav className="category-breadcrumb" aria-label="Breadcrumb">
            <Link to="/">Home</Link>
            <span aria-hidden="true">/</span>
            <Link to="/shop">Shop</Link>
            <span aria-hidden="true">/</span>
            <span className="current">{cat.name}</span>
          </nav>

          <header className="category-header">
            <h1>{cat.h1}</h1>
            <p className="category-intro">{cat.intro}</p>
          </header>

          {items.length > 0 ? (
            <div className="row products-grid">
              {items.map((item, index) => {
                const discount = discountOf(item.price, item.mainPrice);
                return (
                  <div className="col-lg-3 col-md-4 col-sm-6 pb-1" key={item.product_name_key || index}>
                    <div className="modern-product-card">
                      {discount && <div className="discount-badge">-{discount}%</div>}

                      <div className="product-image-container">
                        <Link to={`/item/${item.product_name_key}`}>
                          <img src={item.product_img} alt={item.product_name} loading="lazy" />
                        </Link>
                        <div className="product-overlay"></div>

                        {!item.out_of_stock && (
                          <div className="product-actions">
                            <button
                              className="action-btn cart-btn min-width60"
                              onClick={(e) => handleAddToCart(e, item)}
                              title="Add to Cart"
                            >
                              <i className="fa fa-shopping-cart"></i>
                            </button>
                          </div>
                        )}

                        {item.out_of_stock && (
                          <div className="out-of-stock-overlay">
                            <div className="out-of-stock-text">Out of Stock</div>
                          </div>
                        )}
                      </div>

                      <div className="product-info">
                        <Link
                          className="product-title"
                          to={`/item/${item.product_name_key}`}
                          style={{ textDecoration: "none" }}
                        >
                          {item.product_name}
                        </Link>
                        <div className="product-price-container">
                          <h5 className="current-price">₹{item.price}</h5>
                          {Number(item.mainPrice) > Number(item.price) && (
                            <h6 className="original-price">₹{item.mainPrice}</h6>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            /* An empty grid on an indexed page is worse than no page. Until this
               category has stock online, send the visitor somewhere useful. */
            <div className="category-empty">
              <p>
                Our {cat.name.toLowerCase()} are made to order at the moment and aren't
                listed for online checkout yet.
              </p>
              <div className="category-empty-actions">
                <a
                  className="category-btn primary"
                  href="https://wa.me/916264208349"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Ask on WhatsApp
                </a>
                <Link className="category-btn" to="/wholesale">
                  Request a bulk quote
                </Link>
                <Link className="category-btn" to="/shop">
                  Browse everything
                </Link>
              </div>
            </div>
          )}

          {cat.faqs?.length > 0 && (
            <section className="category-faq">
              <h2>Common questions about {cat.name.toLowerCase()}</h2>
              <dl>
                {cat.faqs.map((f) => (
                  <div className="category-faq-item" key={f.q}>
                    <dt>{f.q}</dt>
                    <dd>{f.a}</dd>
                  </div>
                ))}
              </dl>
            </section>
          )}

          <p className="category-footnote">
            Looking for something else? <Link to="/shop">See the full catalogue</Link> or{" "}
            <Link to="/wholesale">order in bulk from the factory</Link>.
          </p>
        </div>
      </div>
    </>
  );
}
